import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { CoachItemRightsList, CoachRights, mapUserRightsToCoachRights } from './coaches-rights';
import { CurrentUserRolesProvider } from '../../organizations/providers/current-user-roles-provider';
import { Coach } from "../../common/models/coach";
import { Team } from "../../common/models/team";
import { CoachTeam } from "../../common/models/coach-team";
import { AthleteCoach } from "../../common/models/athlete-coach";
import { ConfigProvider } from "../../config-provider.service";
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { LangsProvider } from "../../common/providers/langs-provider";
import { CurrentUser } from "../../common/providers/current-user";
import { TeamsProvider } from "../../teams/providers/teams-provider";
import { map, switchMap } from "rxjs/operators";
import { User } from "../../common/models/user";
import { Athlete } from "../../common/models/athlete";
import { Organization } from "../../common/models/organization";
import { OrganizationRole } from "../../common/models/organization-role";
import { Paginator } from "../../common/models/paginator";
import { UsersProvider } from "../../users/providers/users-provider";
import { forkJoin } from "rxjs";
import { AthleteRow } from "../../athletes/providers/athletes-provider";

@Injectable({
    providedIn: 'root'
})
export class CoachesProvider {
    public Ready: Promise<boolean> = new Promise((resolve, reject) => resolve(false));
    public coachRights: CoachRights = new CoachRights(this._utils);

    private availableCoaches: Coach[] = [];
    private availableTeams: Team[] = [];
    private availableCoachTeamRelations: CoachTeam[] = [];
    private availableAthleteCoachRelations: AthleteCoach[] = [];
    constructor(
        private _conf: ConfigProvider,
        private _utils: UtilsProvider,
        private _api: ApiBaseService,
        private _langs: LangsProvider ,
        private _currentUser: CurrentUser,
        private _teamsProvider: TeamsProvider,
        private _currentUserRoles: CurrentUserRolesProvider,
        private usersProvider: UsersProvider,
        private api: ApiBaseService,
    ) {
        this.Ready = new Promise((resolve, reject) => {

            Promise.all([

                // this.getCoaches(),
                this.getCoachTeamRelations(),
                this.getAthleteCoachRelations(),
                // this._teamsProvider.getTeams(),

            ])
                .then((data: any[]) => {

                    this.availableCoaches = data[0];
                    this.availableCoachTeamRelations = data[1];
                    this.availableAthleteCoachRelations = data[2];
                    this.availableTeams = data[3];

                    resolve(true);
                })
                .catch(reject);
        });
    }
    public get(organizationId:number, userId: number): Observable<User> {
      return this.api.call('get', `organizations/${organizationId}/coaches/${userId}`, {}, this._currentUser.getAccessToken())
        .pipe(
          map((data: any) => User.fromAPI(data))
        );
    }

  public saveCoach(organizationId: number, coachRow: CoachRow): Observable<CoachRow> {
    return this._api.call('post','organizations/' + organizationId + '/coaches/' + (coachRow.user.id ? coachRow.coach.userId : 0),
      coachRow,
      this._currentUser.getAccessToken(),
      false
    ).pipe(
      map((dataSaved: any) => {
        const user = dataSaved.user;
        user.coach = dataSaved.coach;
        user.organizationMemberships = dataSaved.organizationMemberships;
        return this.createCoachRow(organizationId, User.fromAPI(user));
      })
    );
  }

    public getCoaches(organizationId:number, role?: number): Observable<User[]> {
      return this.getCoachesPaginator({
        organizationId: organizationId,
        role: role,
        perPage: 0,
      }).pipe(
        map((paginator: Paginator) => User.fromAPIList(paginator.data))
      );
    }

    public createCoachRow(organizationId: number, user: User): CoachRow {
      return this._coachRowFromUser(user, organizationId);
    }

    private _coachRowFromUser(user: User, organizationId: number): CoachRow {
        return {
          user: User.fromAPI(user),
          coach: Coach.fromAPI(user.coach?? {}),
          athletes: User.fromAPIList(
            user.coach?.athletes
              ?.map((athlete: Athlete) => athlete.user)
              ?.filter((user): user is User => user !== undefined) ?? []
          ),
          athleteNames: user.coach?.athletes?.map((athlete: Athlete) => athlete.user?.firstName + ' ' + athlete.user?.lastName) ?? [],
          teams: Team.fromAPIList(user.coach?.teams ?? []) ?? [],
          teamNames: user.coach?.teams?.map((team: Team) => team.name) ?? [],
          isSuperCoach: user.organizationMemberships.find((membership) => membership.organizationId === organizationId)?.organizationRoles?.some((role: OrganizationRole) => role.id === OrganizationRole.ID_SUPERCOACH) ?? false,
          rights: mapUserRightsToCoachRights(user.rights ?? [])
        };
    }

    public getCoachRow(organizationId: number, userId: number): Observable<CoachRow> {
      return this.api.call('get',`organizations/${organizationId}/coaches/${userId}`, {}, this._currentUser.getAccessToken())
        .pipe(
        map((user: User) => {
          return this._coachRowFromUser(user, organizationId)
        }),
      );
    }

    public getCoachesPaginator({organizationId, pageNumber = 1, orderBy = 'name_asc', perPage = 10, userName, role}: {organizationId: number, pageNumber?: number, orderBy?: string, perPage?: number, userName?: string, role?: number, withSuperCoaches?: boolean}): Observable<Paginator> {
        // check if roles are coach or super coach
        if (role && role !== OrganizationRole.ID_COACH && role !== OrganizationRole.ID_SUPERCOACH) {
          throw new Error('Role not allowed');
        }

        let params: any = {
          page: pageNumber,
          sort: orderBy,
          per_page: perPage,
        }

        if (userName) {
            params.userName = userName;
        }

        // if one role is passed, we can filter by role else we don't filter
        if (role) {
          params.organizationRoleId = role;
        }

        return this.api.call('get',`organizations/${organizationId}/coaches/`, params, this._currentUser.getAccessToken())
          .pipe(
            map((result: Paginator) => result),
          );
    }

    public patchCoach(organizationId: number, userId: number, data:Object): Observable<any> {
      return this.api.call('patch',
        `organizations/${organizationId}/coaches/${userId}`,
        data,
        this._currentUser.getAccessToken()
      );
    }

    public deleteCoach(organizationId: number, userId: number): Observable<void> {
      return this.api.call('delete',`organizations/${organizationId}/coaches/${userId}`, {}, this._currentUser.getAccessToken());
    }


    /*** END NEW REQUESTS ***/
    /*** ****************************** ***/
    /*** ******************************************** ***/
    /*** ****************************** ***/
    /*** OLD REQUESTS ***/


    public getAthleteCoachRelations(): Observable<AthleteCoach[]> {
        return this.api.call('get','athlete-coaches').pipe(
            map((data: any) => AthleteCoach.fromAPIList(data))
        );
    }

    public getCoachTeamRelations(): Observable<CoachTeam[]> {
        return this.api.call('get','coach-teams').pipe(
            map((data: any) => CoachTeam.fromAPIList(data))
        );
    }

    public saveCoachAthletes(organizationId: number, coachRow: CoachRow): Observable<CoachRow> {
      return this.api.call('post',`organizations/${organizationId}/coaches/${coachRow.user.id ?? 0}/athletes`,
        coachRow, this._currentUser.getAccessToken()
      ).pipe(
        switchMap((data: any) => this.getCoachRow(organizationId, data.coach.userId))
      );
    }

    public saveCoachTeams(organizationId: number, removeRelations: boolean, coachRow: CoachRow): Observable<CoachRow> {
      return this.api.call('post',`organizations/${organizationId}/coaches/${coachRow.user.id ?? 0}/teams`,
        {
          atr: coachRow,
          rer: removeRelations
        },
        this._currentUser.getAccessToken()).pipe(
        switchMap((data: any) => this.getCoachRow(organizationId, data.coach.userId))
      );
    }

    /**
     * Return a coach related to an athlete if relation exists.
     * @param user          Coach user
     * @param athlete       Athlete
     * @param organization
     */
    public isCoachOf(user: User, athlete: Athlete, organization: Organization): Observable<boolean> {
      return this.get(organization.id!, user.id!).pipe(
        map((coach: User) => {
          return !!coach.coach?.athletes?.find(ath => ath.id === athlete.id)
        }),
      );
    }

}

export interface CoachRow {
    user: User;
    coach: Coach;
    athletes: User[];
    athleteNames: string[];
    teams: Team[];
    teamNames: string[];
    isSuperCoach: boolean;
    rights?: CoachItemRightsList;
}
